import { classnames } from '@aibees/shared/utils';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  Typography,
} from '@mui/material';
import { memo, useMemo } from 'react';
import { DialogTitle } from '../dialog-title';
import type { TemplateManagementDeleteDialogProps as Props } from './types';

export const TemplateManagementDeleteDialog = memo(
  ({
    className,
    title,
    selectedPreset,
    selectedTemplateId,
    state,
    disabled,
    onClose,
    onDelete,
  }: Props) => {
    const isSelectedTemplate = useMemo(
      () => selectedPreset?.value === selectedTemplateId,
      [selectedPreset, selectedTemplateId]
    );

    const dialogTitle = useMemo(
      () => (isSelectedTemplate ? 'Unsaved Template Removal' : title),
      [isSelectedTemplate, title]
    );

    const dialogDescription = useMemo(
      () =>
        isSelectedTemplate
          ? `You have unsaved changes to the template. Would you like to discard them?`
          : `This action will permanently delete the template. Deleted templates
              cannot be recovered.`,
      [isSelectedTemplate]
    );

    const dialogButtonTitle = useMemo(
      () => (isSelectedTemplate ? 'Save & Apply' : 'Delete Template'),
      [isSelectedTemplate]
    );

    const dialogButtonColor = useMemo(
      () => (isSelectedTemplate ? 'primary' : 'error'),
      [isSelectedTemplate]
    );

    return (
      <Dialog
        className={classnames('TemplateManagementDeleteDialog', className)}
        open={state.value}
        onClose={onClose}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle
          title={
            <Typography color="grey.800" fontWeight={400} variant="body2">
              {dialogTitle}
            </Typography>
          }
          icon="delete"
          closeIconSize={16}
          closeIconColor="grey.700"
          onClose={onClose}
        />

        <DialogContent>
          <Typography variant="body3" fontWeight={500} color="grey.700">
            {dialogDescription}
          </Typography>
        </DialogContent>

        <DialogActions>
          <Button size="small" variant="text" onClick={onClose}>
            Cancel
          </Button>
          <Button
            size="small"
            variant="contained"
            color={dialogButtonColor}
            onClick={onDelete}
            disabled={disabled}
          >
            {dialogButtonTitle}
          </Button>
        </DialogActions>
      </Dialog>
    );
  }
);

TemplateManagementDeleteDialog.displayName = 'TemplateManagementDeleteDialog';
